import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2, Building2, Star } from 'lucide-react';
import toast from 'react-hot-toast';
import vendorService from '../../services/vendorService';

const emptyAccount = () => ({ bank_name: '', account_name: '', account_number: '', currency: 'NGN', is_primary: false });
const emptyVendor = () => ({
  company_name: '',
  contact_person: '',
  email: '',
  phone: '',
  address: '',
  tax_number: '',
  registration_number: '',
  category: '',
  accounts: [emptyAccount()],
});

const VendorManagementModal = ({ onClose }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // vendor being created/edited, or null
  const [form, setForm] = useState(emptyVendor());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      setLoading(true);
      const res = await vendorService.listVendors();
      setVendors(res.data.data?.vendors || []);
    } catch (error) {
      console.error('Load vendors error:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const startCreate = () => {
    setForm(emptyVendor());
    setEditing('new');
  };

  const startEdit = (vendor) => {
    setForm({
      company_name: vendor.company_name || '',
      contact_person: vendor.contact_person || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: (vendor.address && typeof vendor.address === 'object' ? vendor.address.text : vendor.address) || '',
      tax_number: vendor.tax_number || '',
      registration_number: vendor.registration_number || '',
      category: vendor.category || '',
      accounts: [],
    });
    setEditing(vendor.id);
  };

  const updateAccountField = (index, field, value) => {
    setForm((prev) => {
      const accounts = [...prev.accounts];
      accounts[index] = { ...accounts[index], [field]: value };
      if (field === 'is_primary' && value) {
        accounts.forEach((a, i) => {
          if (i !== index) a.is_primary = false;
        });
      }
      return { ...prev, accounts };
    });
  };

  const addAccountRow = () => {
    setForm((prev) => ({ ...prev, accounts: [...prev.accounts, emptyAccount()] }));
  };

  const removeAccountRow = (index) => {
    setForm((prev) => ({ ...prev, accounts: prev.accounts.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) {
      toast.error('Vendor company name is required');
      return;
    }
    try {
      setSaving(true);
      if (editing === 'new') {
        await vendorService.createVendor(form);
        toast.success('Vendor created');
      } else {
        await vendorService.updateVendor(editing, form);
        toast.success('Vendor updated');
      }
      setEditing(null);
      loadVendors();
    } catch (error) {
      console.error('Save vendor error:', error);
      toast.error(error.response?.data?.message || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (vendor) => {
    if (!window.confirm(`Deactivate vendor "${vendor.company_name}"?`)) return;
    try {
      await vendorService.deactivateVendor(vendor.id);
      toast.success('Vendor deactivated');
      loadVendors();
    } catch (error) {
      toast.error('Failed to deactivate vendor');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Vendors
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!editing && (
          <div className="mb-4 flex justify-end">
            <button
              onClick={startCreate}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> New Vendor
            </button>
          </div>
        )}

        {editing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Company Name *</label>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Contact Person</label>
                <input
                  type="text"
                  value={form.contact_person}
                  onChange={(e) => setForm((p) => ({ ...p, contact_person: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tax / Registration No.</label>
                <input
                  type="text"
                  value={form.tax_number}
                  onChange={(e) => setForm((p) => ({ ...p, tax_number: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g. Office Supplies"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
              </div>
            </div>

            {editing === 'new' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Bank Accounts <span className="text-gray-400 font-normal">(a vendor can have more than one)</span>
                  </label>
                  <button
                    onClick={addAccountRow}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add Account
                  </button>
                </div>
                <div className="space-y-2">
                  {form.accounts.map((acc, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-2 rounded border">
                      <input
                        placeholder="Bank Name"
                        value={acc.bank_name}
                        onChange={(e) => updateAccountField(i, 'bank_name', e.target.value)}
                        className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-xs"
                      />
                      <input
                        placeholder="Account Name"
                        value={acc.account_name}
                        onChange={(e) => updateAccountField(i, 'account_name', e.target.value)}
                        className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-xs"
                      />
                      <input
                        placeholder="Account Number"
                        value={acc.account_number}
                        onChange={(e) => updateAccountField(i, 'account_number', e.target.value)}
                        className="col-span-3 px-2 py-1.5 border border-gray-300 rounded text-xs"
                      />
                      <input
                        placeholder="Currency"
                        value={acc.currency}
                        onChange={(e) => updateAccountField(i, 'currency', e.target.value)}
                        className="col-span-1 px-2 py-1.5 border border-gray-300 rounded text-xs"
                      />
                      <button
                        title="Set as primary account"
                        onClick={() => updateAccountField(i, 'is_primary', !acc.is_primary)}
                        className={`col-span-1 flex justify-center ${acc.is_primary ? 'text-yellow-500' : 'text-gray-300'}`}
                      >
                        <Star className="h-4 w-4" fill={acc.is_primary ? 'currentColor' : 'none'} />
                      </button>
                      <button
                        onClick={() => removeAccountRow(i)}
                        className="col-span-1 flex justify-center text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Vendor'}
              </button>
            </div>
          </div>
        ) : loading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Loading vendors...</div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">No vendors yet. Click "New Vendor" to add one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Accounts</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vendors.map((v) => (
                  <tr key={v.id}>
                    <td className="px-3 py-2 font-medium text-gray-900">{v.company_name}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {v.contact_person}
                      {v.email ? ` · ${v.email}` : ''}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {(v.Accounts || []).length} account{(v.Accounts || []).length !== 1 ? 's' : ''}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          v.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {v.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right space-x-2">
                      <button onClick={() => startEdit(v)} className="text-blue-600 hover:text-blue-800 text-xs">
                        Edit
                      </button>
                      <button onClick={() => handleDeactivate(v)} className="text-red-600 hover:text-red-800 text-xs">
                        Deactivate
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorManagementModal;
